import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Wallet, DollarSign, Plus, X, Search, CloudRain } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { PlayerCreditHistory } from "../components/PlayerCreditHistory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCredits() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/credits`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load credits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const rows = (data?.rows || []).filter((r) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      (r.parent_name || "").toLowerCase().includes(q) ||
      (r.parent_email || "").toLowerCase().includes(q) ||
      (r.children || []).some((c) => (c || "").toLowerCase().includes(q))
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-black text-slate-100 p-4 md:p-8">
      <div className="max-w-[1400px] mx-auto">
        <Link to="/admin/payments" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="back-to-hub">
          <ArrowLeft className="w-4 h-4" /> Back to Payments
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <Wallet className="w-9 h-9 text-teal-400" />
              Credits Ledger
            </h1>
            <p className="text-slate-400 mt-1">
              Every credit a family holds — called-off sessions, overpayments and manual
              adjustments — and what is still unused.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {data && (
              <Card className="px-4 py-3 bg-teal-500/10 border-teal-500/40">
                <p className="text-xs text-teal-200 uppercase tracking-wide">Total outstanding</p>
                <p className="text-2xl font-bold text-teal-200 font-mono" data-testid="total-outstanding">
                  ${(data.total_outstanding || 0).toFixed(2)}
                </p>
                <p className="text-[10px] text-teal-200/70">across {data.parents_with_balance} parent{data.parents_with_balance === 1 ? "" : "s"}</p>
              </Card>
            )}
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-teal-600 hover:bg-teal-500 text-white"
              data-testid="add-credit-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add credit
            </Button>
            <Link
              to="/admin/rain-out"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm bg-sky-600/20 border border-sky-500/40 text-sky-200 hover:bg-sky-600/30"
              data-testid="rain-out-link"
              title="Cancel one date across every programme and credit everyone who paid for it"
            >
              <CloudRain className="w-4 h-4" /> Call off a session day
            </Link>
            <a
              href="/admin/billing-adjustments"
              className="inline-flex items-center px-3 py-2 rounded-md text-sm border border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="billing-adjustments-link"
              title="Unified audit trail: every override, credit grant, credit use, refund, waiver"
            >
              Billing audit trail →
            </a>
          </div>
        </div>

        <div className="mb-4">
          <Input
            placeholder="Search by parent, child name or email…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-900/50 border-slate-700 max-w-md"
            data-testid="credits-search"
          />
        </div>

        <Card className="bg-slate-900/40 border-slate-800 overflow-hidden">
          {loading && (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-teal-400 mx-auto" />
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="py-12 text-center text-slate-500">
              <DollarSign className="w-10 h-10 mx-auto text-slate-600 mb-2" />
              <p>No credits recorded yet.</p>
              <p className="text-xs mt-1">Credits are posted automatically when an overpayment is approved in the Payments hub, or manually via the “Add credit” button above.</p>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/80 border-b border-slate-800">
                  <tr className="text-left text-xs uppercase text-slate-400">
                    <th className="p-3">Parent</th>
                    <th className="p-3 text-right">Balance</th>
                    <th className="p-3">Where from</th>
                    <th className="p-3 text-right">Total credited</th>
                    <th className="p-3 text-right">Total used</th>
                    <th className="p-3 text-right">Postings</th>
                    <th className="p-3">Last activity</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.parent_id || r.parent_email} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`credit-row-${r.parent_id || r.parent_email}`}>
                      <td className="p-3">
                        <p className="text-white font-medium">{r.parent_name || <span className="text-slate-500 italic">(no name on file)</span>}</p>
                        <p className="text-xs text-slate-400">{r.parent_email || "—"}</p>
                        {(r.children || []).length > 0 && (
                          <p className="text-xs text-teal-300/80 mt-0.5"
                            data-testid={`credit-children-${r.parent_id || r.parent_email}`}>
                            for {r.children.join(", ")}
                          </p>
                        )}
                      </td>
                      <td className={`p-3 text-right font-mono font-semibold ${r.balance > 0 ? "text-teal-300" : r.balance < 0 ? "text-red-300" : "text-slate-400"}`}>
                        ${r.balance.toFixed(2)}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(r.sources || []).length === 0 && (
                            <span className="text-xs text-slate-500">—</span>
                          )}
                          {(r.sources || []).includes("cancelled_session") && (
                            <span
                              className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] text-sky-300"
                              data-testid={`credit-source-rainout-${r.parent_id || r.parent_email}`}
                            >
                              Called-off session
                              {r.cancellation_balance > 0 ? ` $${r.cancellation_balance.toFixed(2)}` : ""}
                            </span>
                          )}
                          {(r.sources || []).includes("admin_grant") && (
                            <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] text-violet-300">
                              Admin grant
                            </span>
                          )}
                          {(r.sources || []).includes("wallet") && (
                            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300">
                              Overpayment
                              {r.wallet_balance > 0 ? ` $${r.wallet_balance.toFixed(2)}` : ""}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono text-slate-300">${r.credited.toFixed(2)}</td>
                      <td className="p-3 text-right font-mono text-slate-400">${r.used.toFixed(2)}</td>
                      <td className="p-3 text-right font-mono text-slate-400">{r.posting_count}</td>
                      <td className="p-3 text-xs text-slate-400">
                        {r.last_activity ? new Date(r.last_activity).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-[11px] border-slate-700 text-slate-200"
                          disabled={!r.parent_id}
                          onClick={() => setHistoryFor({ parentId: r.parent_id, name: r.parent_name || r.parent_email })}
                          data-testid={`credit-history-btn-${r.parent_id || r.parent_email}`}
                        >
                          History
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="mt-4 text-xs text-slate-500">
          Credits are applied to the next unpaid invoice automatically. Tap “History” on a family to
          see every credit, every no-credit call and who decided it.
        </p>
      </div>

      {historyFor && (
        <PlayerCreditHistory
          parentId={historyFor.parentId}
          title={historyFor.name}
          onClose={() => setHistoryFor(null)}
        />
      )}

      {showAddModal && (
        <AddCreditModal
          onClose={() => setShowAddModal(false)}
          onSaved={async () => { setShowAddModal(false); await reload(); }}
        />
      )}
    </div>
  );
}

function AddCreditModal({ onClose, onSaved }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const search = async () => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/academy/admin/credits/parent-search`, {
        params: { q: query.trim() },
        withCredentials: true,
      });
      setResults(r.data?.results || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const submit = async () => {
    if (!picked) { toast.error("Pick a parent first"); return; }
    const parsed = parseFloat(amount);
    if (!parsed || isNaN(parsed) || parsed === 0) { toast.error("Amount must be non-zero"); return; }
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/academy/admin/credits/parent-grant`, {
        parent_id: picked.id,
        parent_email: picked.email,
        amount: parsed,
        reason: reason.trim(),
        note: note.trim(),
      }, { withCredentials: true });
      toast.success(`$${parsed.toFixed(2)} ${parsed > 0 ? "credited to" : "deducted from"} ${picked.display_name || picked.name || picked.email}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post credit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-credit-modal">
      <Card className="w-full max-w-lg bg-slate-900 border-slate-800 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-teal-400" /> Post a credit
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {!picked && (
          <div className="space-y-3 mb-4">
            <label className="text-xs uppercase tracking-wide text-slate-400">1. Find parent</label>
            <div className="flex gap-2">
              <Input
                placeholder="Search name, email or membership number…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
                className="bg-slate-950 border-slate-700"
                data-testid="parent-search-input"
              />
              <Button onClick={search} disabled={searching || query.trim().length < 2} className="bg-slate-700 hover:bg-slate-600">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {results.length > 0 && (
              <div className="border border-slate-800 rounded-lg divide-y divide-slate-800 max-h-60 overflow-y-auto">
                {results.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setPicked(u)}
                    className="w-full text-left p-3 hover:bg-slate-800/60 transition-colors"
                    data-testid={`parent-pick-${u.id}`}
                  >
                    <p className="text-white text-sm font-medium">{u.display_name || u.name || u.email}</p>
                    <p className="text-xs text-slate-400">{u.email} {u.membership_number ? `· ${u.membership_number}` : ""}</p>
                  </button>
                ))}
              </div>
            )}
            {results.length === 0 && query.trim().length >= 2 && !searching && (
              <p className="text-xs text-slate-500">No matches. Try a different query.</p>
            )}
          </div>
        )}

        {picked && (
          <div className="space-y-4">
            <div className="p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-teal-200">Parent</p>
                <p className="text-white font-medium">{picked.display_name || picked.name}</p>
                <p className="text-xs text-slate-300">{picked.email}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPicked(null)} className="text-teal-200 hover:text-white">change</Button>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400">Amount (SGD) — negative to deduct</label>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g. 50.00 or -25.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-slate-950 border-slate-700 mt-1 font-mono"
                data-testid="credit-amount-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400">Reason (required)</label>
              <Input
                placeholder="e.g. cancelled session refund / goodwill / cash payout"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="bg-slate-950 border-slate-700 mt-1"
                data-testid="credit-reason-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400">Note (optional)</label>
              <textarea
                rows={3}
                placeholder="Anything else for the audit log…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-md p-2 mt-1 text-sm text-white"
                data-testid="credit-note-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="flex-1 bg-teal-600 hover:bg-teal-500"
                data-testid="credit-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post credit"}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
